import { useState } from 'react'
import type { CSSProperties } from 'react'
import { Draggable, Droppable } from '@hello-pangea/dnd'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import ProjectTaskCard from '@/frontend/features/Fde/ProjectTaskCard'
import ProjectTaskRow from '@/frontend/features/Fde/ProjectTaskRow'
import ProjectTaskMenu from '@/frontend/features/Fde/ProjectTaskMenu'
import type { TaskSurfaceProps } from '@/frontend/features/Fde/ProjectTaskCanvas'

export default function TaskCategoryGroups(props: TaskSurfaceProps & { board: boolean }) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const names = new Map(
    props.tasks.map((task) => [
      task.category?.id ?? '',
      task.category?.name ?? props.copy.management.no_category,
    ]),
  )
  const categories = Array.from(names, ([id, name]) => ({ id, name })).sort((a, b) =>
    a.name.localeCompare(b.name),
  )
  return (
    <div className={cn('h-full overflow-auto p-4', props.board && 'flex items-start gap-4')}>
      {categories.map((category) => {
        const tasks = props.tasks.filter((task) => (task.task_category_id ?? '') === category.id)
        const closed = collapsed.has(category.id)
        return (
          <section
            key={category.id}
            aria-label={category.name}
            className={cn('mb-4', props.board && 'w-72 shrink-0')}
          >
            <button
              type="button"
              aria-expanded={!closed}
              onClick={() =>
                setCollapsed((current) => {
                  const next = new Set(current)
                  if (next.has(category.id)) next.delete(category.id)
                  else next.add(category.id)
                  return next
                })
              }
              className="mb-2 flex w-full items-center gap-2 py-2 text-left text-xs font-medium"
            >
              <ChevronRight
                className={cn('size-3.5 transition-transform', !closed && 'rotate-90')}
              />
              {category.name}
              <span className="font-normal tabular-nums text-muted-foreground">{tasks.length}</span>
            </button>
            {!closed && (
              <Droppable
                droppableId={`category:${category.id}`}
                isDropDisabled={!props.canManage || props.busy}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      'min-h-16',
                      props.board ? 'space-y-2' : 'divide-y border-y',
                      snapshot.isDraggingOver && 'bg-primary/5',
                    )}
                  >
                    {tasks.map((task, index) => (
                      <Draggable
                        key={task.id}
                        draggableId={task.id}
                        index={index}
                        isDragDisabled={!props.canManage || props.busy}
                        disableInteractiveElementBlocking
                      >
                        {(drag) => (
                          <ProjectTaskMenu
                            task={task}
                            canManage={props.canManage}
                            busy={props.busy}
                            onSelect={() => props.onSelect(task.id)}
                            onPatch={props.onPatch}
                            onCopy={props.onCopy}
                            copy={props.copy}
                          >
                            <div
                              ref={drag.innerRef}
                              {...drag.draggableProps}
                              style={drag.draggableProps.style as CSSProperties}
                              className="bg-background"
                            >
                              {props.board ? (
                                <ProjectTaskCard
                                  task={task}
                                  overdue={props.overdueIds.has(task.id)}
                                  onSelect={() => props.onSelect(task.id)}
                                  copy={props.copy}
                                  dragHandleProps={drag.dragHandleProps}
                                />
                              ) : (
                                <ProjectTaskRow
                                  task={task}
                                  overdue={props.overdueIds.has(task.id)}
                                  onSelect={() => props.onSelect(task.id)}
                                  copy={props.copy}
                                  dragHandleProps={drag.dragHandleProps}
                                />
                              )}
                            </div>
                          </ProjectTaskMenu>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </section>
        )
      })}
    </div>
  )
}
